// 🚀 REAL-TIME NOTIFICATION SERVICE - WhatsApp-Style Alerts
import { supabase } from '../../integrations/supabase/client';
import { userGestureDetector } from '../../utils/userGestureDetector';
import { badgeService } from '@/services/push/badgeService';
import { Capacitor } from '@capacitor/core';

export interface NotificationOptions {
  title: string;
  body: string;
  icon?: string;
  badge?: string;
  tag?: string;
  requireInteraction?: boolean;
  silent?: boolean;
  sound?: boolean;
}

export interface MessageNotification {
  id: string;
  chatId: string;
  senderId: string;
  senderName: string;
  content: string;
  timestamp: Date;
  chatType: 'match' | 'friend';
}

export interface ConnectionRequestNotification {
  id: string;
  requesterId: string;
  requesterName: string;
  requesterPhoto?: string;
  inviteCode: string;
  timestamp: Date;
}

class NotificationService {
  private permission: NotificationPermission = 'default';
  private audioContext: AudioContext | null = null;
  private notificationSound: AudioBuffer | null = null;
  private isInitialized = false;

  async initialize(): Promise<boolean> {
    try {
      // 🚀 V209.3: Only request permission if not already granted/denied
      // This prevents unnecessary permission prompts
      if ('Notification' in window) {
        const currentPermission = Notification.permission;
        if (currentPermission === 'default') {
          // Only request if user hasn't made a decision yet
          // But this should only be called on user gesture, not on page load
          this.permission = await Notification.requestPermission();
        } else {
          // Use existing permission
          this.permission = currentPermission;
        }
      }

      // Initialize audio for notification sounds (non-blocking)
      if ('AudioContext' in window || 'webkitAudioContext' in window) {
        this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        await this.loadNotificationSound();
      }

      this.isInitialized = true;
      return this.permission === 'granted';
    } catch (error) {
      console.error('❌ Failed to initialize notification service:', error);
      return false;
    }
  }

  private async loadNotificationSound(): Promise<void> {
    try {
      // Create a simple notification sound using Web Audio API
      if (!this.audioContext) return;

      const sampleRate = this.audioContext.sampleRate;
      const duration = 0.3; // 300ms
      const buffer = this.audioContext.createBuffer(1, sampleRate * duration, sampleRate);
      const data = buffer.getChannelData(0);

      // Generate a pleasant notification tone (two-tone chime)
      for (let i = 0; i < buffer.length; i++) {
        const t = i / sampleRate;
        const freq1 = 800; // First tone
        const freq2 = 600; // Second tone
        
        if (t < 0.15) {
          data[i] = Math.sin(2 * Math.PI * freq1 * t) * Math.exp(-t * 3) * 0.3;
        } else {
          data[i] = Math.sin(2 * Math.PI * freq2 * t) * Math.exp(-(t - 0.15) * 3) * 0.3;
        }
      }

      this.notificationSound = buffer;
      // Notification sound loaded
    } catch (error) {
      console.error('❌ Failed to load notification sound:', error);
    }
  }

  async playNotificationSound(): Promise<void> {
    try {
      if (!this.audioContext || !this.notificationSound) return;

      // V320.RELOAD.5: Only resume audio context if user has gestured (prevents Chrome intervention warning)
      const resumed = await userGestureDetector.safeResumeAudioContext(this.audioContext);
      if (!resumed) {
        console.log('⚠️ V320.RELOAD.5: Skipping notification sound - no user gesture yet');
        return;
      }

      const source = this.audioContext.createBufferSource();
      source.buffer = this.notificationSound;
      source.connect(this.audioContext.destination);
      source.start();
    } catch (error) {
      console.error('❌ Failed to play notification sound:', error);
    }
  }

  async showMessageNotification(notification: MessageNotification): Promise<void> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    if (this.permission !== 'granted') {
      // Notifications not permitted
      return;
    }

    try {
      // Don't show notification if the page is visible and focused
      if (document.visibilityState === 'visible' && document.hasFocus()) {
        return;
      }

      const options: NotificationOptions = {
        title: notification.senderName,
        body: this.truncateMessage(notification.content),
        icon: '/blissnet-master-1024.png',
        badge: '/blissnet-master-1024.png',
        tag: `message-${notification.chatId}`,
        requireInteraction: false,
        silent: false,
        sound: true
      };

      const browserNotification = new Notification(options.title, {
        body: options.body,
        icon: options.icon,
        badge: options.badge,
        tag: options.tag,
        requireInteraction: options.requireInteraction,
        silent: options.silent
      });

      // Play sound
      if (options.sound) {
        await this.playNotificationSound();
      }

      // Auto-close after 5 seconds
      setTimeout(() => {
        browserNotification.close();
      }, 5000);

      // Handle notification click
      browserNotification.onclick = () => {
        window.focus();
        // Navigate to chat (this would need to be handled by the calling component)
        browserNotification.close();
      };

    } catch (error) {
      console.error('Failed to show message notification:', error);
    }
  }

  private truncateMessage(message: string, maxLength: number = 100): string {
    if (message.length <= maxLength) return message;
    return message.substring(0, maxLength) + '...';
  }

  async showTypingNotification(senderName: string, chatId: string): Promise<void> {
    if (this.permission !== 'granted') return;

    // Only show typing notifications if page is not focused
    if (document.visibilityState === 'visible' && document.hasFocus()) {
      return;
    }

    try {
      const notification = new Notification(`${senderName} is typing...`, {
        icon: '/blissnet-master-1024.png',
        tag: `typing-${chatId}`,
        silent: true,
        requireInteraction: false
      });

      // Auto-close typing notification quickly
      setTimeout(() => {
        notification.close();
      }, 2000);
    } catch (error) {
      console.error('Failed to show typing notification:', error);
    }
  }

  // Update browser badge (for PWA support)
  updateBadgeCount(count: number): void {
    if (Capacitor.isNativePlatform()) {
      return;
    }
    void badgeService.setCount(count);
  }

  // Clear all notifications for a specific chat
  clearChatNotifications(chatId: string): void {
    // This is a limitation of the Web Notifications API
    // We can't programmatically clear specific notifications
    // But we can use tags to replace them
    console.log('🔔 Clearing notifications for chat:', chatId);
  }

  // 🔒 SECURITY: Send connection request notification
  async sendConnectionRequestNotification(notification: ConnectionRequestNotification): Promise<void> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    if (this.permission !== 'granted') {
      console.log('🔔 Notifications not permitted, skipping connection request notification');
      return;
    }

    try {
      // Don't show notification if the page is visible and focused
      if (document.visibilityState === 'visible' && document.hasFocus()) {
        return;
      }

      const options: NotificationOptions = {
        title: '🤝 New Connection Request',
        body: `${notification.requesterName} wants to connect with you`,
        icon: notification.requesterPhoto || '/blissnet-master-1024.png',
        badge: '/blissnet-master-1024.png',
        tag: `connection-request-${notification.id}`,
        requireInteraction: true, // Keep notification until user interacts
        silent: false,
        sound: true
      };

      const browserNotification = new Notification(options.title, {
        body: options.body,
        icon: options.icon,
        badge: options.badge,
        tag: options.tag,
        requireInteraction: options.requireInteraction,
        silent: options.silent
      });

      // Play sound
      if (options.sound) {
        await this.playNotificationSound();
      }

      // Handle notification click - navigate to alerts tab
      browserNotification.onclick = () => {
        window.focus();
        // Navigate to alerts tab on Network page
        window.location.href = '/network?tab=alerts';
        browserNotification.close();
      };

    } catch (error) {
      console.error('Failed to send connection request notification:', error);
    }
  }

  // Send connection approved notification
  async sendConnectionApprovedNotification(approverName: string, approverPhoto?: string): Promise<void> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    if (this.permission !== 'granted') {
      // Notifications not permitted
      return;
    }

    try {
      const options: NotificationOptions = {
        title: '✅ Connection Approved!',
        body: `${approverName} accepted your connection request`,
        icon: approverPhoto || '/blissnet-master-1024.png',
        badge: '/blissnet-master-1024.png',
        tag: `connection-approved-${Date.now()}`,
        requireInteraction: false,
        silent: false,
        sound: true
      };

      const browserNotification = new Notification(options.title, {
        body: options.body,
        icon: options.icon,
        badge: options.badge,
        tag: options.tag,
        requireInteraction: options.requireInteraction,
        silent: options.silent
      });

      // Play sound
      if (options.sound) {
        await this.playNotificationSound();
      }

      // Handle notification click - navigate to network
      browserNotification.onclick = () => {
        window.focus();
        window.location.href = '/network';
        browserNotification.close();
      };

      // Connection approved notification sent

    } catch (error) {
      console.error('❌ Failed to send connection approved notification:', error);
    }
  }

  getPermissionStatus(): NotificationPermission {
    return this.permission;
  }

  isSupported(): boolean {
    return 'Notification' in window;
  }
}

// Export singleton instance
export const notificationService = new NotificationService();

// 🚀 V209.3: REMOVED auto-initialize - notification permission should only be requested on user gesture
// This improves Best Practices score (no permission request on page load)
// Notification service will be initialized when user explicitly requests notifications
// See: useNotifications hook or explicit user action

